import React, { useCallback, useState, useEffect, useRef } from "react";
import {
  Dialog,
  useTheme,
  Box,
  IconButton,
  Divider,
  Grid2,
  Paper,
  Checkbox,
  CircularProgress,
  Collapse,
  InputAdornment,
} from "@mui/material";
import FormSmallTextField from "../../../../../views/components/common/form/FormTextField";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import InlineFormError from "../../../../../views/components/common/form/InlineFormError";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import { Employee, Resetpassword } from "../../../../../models/Employees/Employee";
import { EmployeePermisson } from "../../../../../models/Employees/EmployeePermisson";
import { TeamWithPermissions } from "../../../../../models/Employees/TeamWithPermissions";
import CloseIcon from "@mui/icons-material/Close";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import SearchIcon from "@mui/icons-material/Search";
import CustomTextField from "../../../../../views/components/common/textFields/TextField";
import { useDebounce } from "../../../../../views/components/common/hooks/Debounce";
import AppSearchDropdownMulti from "../../../../../views/components/common/filter/AppSearchDropdownMulti";
import {
  PermissionProject,
  PERMISSION_PROJECT_OPTIONS,
} from "../../../../../models/Employees/PermissionProject";
import { FilterValue } from "../../../../../models/Filters/FilterValue";
import CopyEmployeePermissions from "./CopyEmployeePermissions";
import type { Theme } from "@mui/material/styles";

type PermissionEditState = "saved" | "new" | "removed" | "idle";

/**
 * Classifies a permission against the copy loaded for this employee.
 * A saved tick stays distinct from a new tick, and an unchecked saved
 * permission stays distinct from one that was never granted.
 */
function permissionEditState(
  checked: boolean,
  saved: boolean,
): PermissionEditState {
  if (checked && saved) return "saved";
  if (checked) return "new";
  if (saved) return "removed";
  return "idle";
}

function permissionBorderColor(theme: Theme, state: PermissionEditState): string {
  const colors = theme.palette.customColors;
  if (state === "removed") return colors.errorRed;
  if (state === "new") return colors.purple;
  if (state === "saved") return colors.successGreen;
  return colors.greyBorder;
}

function isManagerFlag(value: unknown): boolean {
  return value === true || value === 1 || value === "1";
}

function permissionTeamLabel(teamNames: string[]): string {
  if (teamNames.length <= 2) return teamNames.join(" · ");
  return `${teamNames.slice(0, 2).join(" · ")} +${teamNames.length - 2}`;
}

function sameNumberSet(left: number[], right: number[]): boolean {
  if (left.length !== right.length) return false;
  const rightIds = new Set(right);
  return left.every((id) => rightIds.has(id));
}

function assignmentChanged(current: Employee, baseline: Employee): boolean {
  const currentPermissions = (current.permissions ?? []).map((permission) =>
    Number(permission.role_id),
  );
  const baselinePermissions = (baseline.permissions ?? []).map((permission) =>
    Number(permission.role_id),
  );
  if (!sameNumberSet(currentPermissions, baselinePermissions)) return true;
  const teamSignature = (
    teams: { id: number; is_manager?: boolean | number }[] | undefined,
  ) =>
    (teams ?? [])
      .map((team) => `${Number(team.id)}:${isManagerFlag(team.is_manager) ? 1 : 0}`)
      .sort()
      .join(",");
  return teamSignature(current.teams) !== teamSignature(baseline.teams);
}

function permissionCheckboxSx(theme: Theme, state: PermissionEditState) {
  const colors = theme.palette.customColors;
  const checkedColor = state === "new" ? colors.purple : colors.successGreen;
  return {
    p: 0.5,
    color: state === "removed" ? colors.errorRed : colors.greyTextColor,
    "&.Mui-checked, &.MuiCheckbox-indeterminate": {
      color: checkedColor,
    },
  };
}

function PermissionCatalogSearch({
  onDebouncedChange,
}: {
  onDebouncedChange: (query: string) => void;
}) {
  const theme = useTheme();
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebounce(query, 400);

  useEffect(() => {
    onDebouncedChange(debouncedQuery);
  }, [debouncedQuery, onDebouncedChange]);

  return (
    <Box sx={{ width: { xs: "100%", sm: 280 }, flexShrink: 0 }}>
      <CustomTextField
        width="100%"
        placeholder="Search permissions"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        startAdornment={
          <InputAdornment position="start">
            <SearchIcon
              sx={{
                fontSize: 18,
                color: theme.palette.customColors.greyTextColor,
              }}
            />
          </InputAdornment>
        }
      />
    </Box>
  );
}

interface EditEmployeePopupProps {
  open: boolean;
  /** Dialog stays the default. Page renders the same form as its own screen. */
  layout?: "dialog" | "page";
  employeeData?: Employee;
  teamsWithPermissions?: TeamWithPermissions[];
  /** Team ids whose permissions are still being fetched. */
  loadingPermissionTeamIds?: number[];
  /** Loads permissions for teams the user just checked. */
  onLoadTeamPermissions?: (teamIds: number[]) => Promise<void>;
  canEditTeam?: boolean;
  /** The All permission. Shows every visible team, without allowing membership edits. */
  canViewOtherTeams?: boolean;
  manageableTeamIds?: number[];
  /** Team ids this person may see. Null shows every team in the catalog. */
  visibleTeamIds?: number[] | null;
  isViewOnly?: boolean;
  isSuperAdminManager?: boolean;
  isAddMode?: boolean;
  discountPermission?: boolean;
  permissionProject?: PermissionProject;
  resetPw?: (email: Resetpassword | any) => void | Promise<void>;
  onClose?: () => void;
  onSave?: (updatedEmployee: Employee) => void;
  saveError?: string | null;
  isSaving?: boolean;
  copyResetKey?: number;
  onPermissionProjectChange?: (project: PermissionProject) => void;
  onSearchEmployees?: (search: string) => Promise<FilterValue[]>;
  onLoadCopySource?: (employeeId: number) => Promise<Employee>;
  permissionsLoading?: boolean;
  permissionsError?: string | null;
}

const EditEmployeePopup: React.FC<EditEmployeePopupProps> = ({
  open,
  layout = "dialog",
  employeeData,
  teamsWithPermissions = [],
  loadingPermissionTeamIds = [],
  onLoadTeamPermissions,
  canEditTeam,
  canViewOtherTeams = false,
  manageableTeamIds = [],
  visibleTeamIds = null,
  isViewOnly,
  discountPermission,
  isAddMode,
  resetPw,
  isSuperAdminManager,
  permissionProject = "swageazy",
  onClose,
  onSave,
  saveError = null,
  isSaving = false,
  copyResetKey = 0,
  onPermissionProjectChange,
  onSearchEmployees,
  onLoadCopySource,
  permissionsLoading = false,
  permissionsError = null,
}) => {
  const theme = useTheme();
  const blankEmployee = useCallback(
    (): Employee => (isAddMode ? { calling_code: "+91" } : {}) as Employee,
    [isAddMode],
  );
  const [employeeDetails, setEmployeeDetails] = useState<Employee>(
    () => employeeData || blankEmployee(),
  );
  const [permissionQuery, setPermissionQuery] = useState("");
  const [teamsOpen, setTeamsOpen] = useState(true);
  const [permissionsOpen, setPermissionsOpen] = useState(true);
  const [assignmentError, setAssignmentError] = useState<string | null>(null);
  const setDebouncedPermissionQuery = useCallback((query: string) => {
    setPermissionQuery((current) => (current === query ? current : query));
  }, []);
  const [resetPasswordMessage, setResetPasswordMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);
  const permissionProjectRef = useRef(permissionProject);
  const employeeDetailsRef = useRef(employeeDetails);
  const copyBaselineRef = useRef<
    Record<number, { teamSelected: boolean; roleIds: number[] }>
  >({});
  employeeDetailsRef.current = employeeDetails;

  useEffect(() => {
    const teamCount = (employeeDetails.teams ?? []).length;
    const permissionCount = (employeeDetails.permissions ?? []).length;
    if (teamCount > 0 && permissionCount > 0) setAssignmentError(null);
  }, [employeeDetails.teams, employeeDetails.permissions]);

  useEffect(() => {
    if (!resetPasswordMessage) return;
    const timer = window.setTimeout(() => setResetPasswordMessage(null), 4000);
    return () => window.clearTimeout(timer);
  }, [resetPasswordMessage]);

  useEffect(() => {
    if (!open) return;
    copyBaselineRef.current = {};
    setEmployeeDetails(employeeData || blankEmployee());
  }, [open, employeeData, blankEmployee]);

  useEffect(() => {
    copyBaselineRef.current = {};
  }, [copyResetKey]);

  useEffect(() => {
    if (!isAddMode || permissionProjectRef.current === permissionProject) {
      permissionProjectRef.current = permissionProject;
      return;
    }
    permissionProjectRef.current = permissionProject;
    setEmployeeDetails((prev) => ({
      ...prev,
      teams: [],
      permissions: [],
    }));
    copyBaselineRef.current = {};
    setPermissionQuery("");
  }, [isAddMode, permissionProject]);

  const handleFieldChange = (
    fieldName: keyof Employee,
    value: string | number,
  ) => {
    setEmployeeDetails((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  const profileLocked =
    Boolean(isViewOnly) || (!isAddMode && !canEditTeam);
  const teamsLocked =
    Boolean(isViewOnly) || (!isSuperAdminManager && !canEditTeam);
  const permissionsLocked = Boolean(isViewOnly) || permissionsLoading;
  const canEditTeamPermissions = (teamId: number) =>
    Boolean(canEditTeam) || manageableTeamIds.includes(teamId);
  const isTeamVisible = (teamId: number) =>
    visibleTeamIds == null || visibleTeamIds.includes(teamId);
  const selectedTeamIds = new Set(
    (employeeDetails.teams ?? [])
      .map((team) => Number(team.id))
      .filter((teamId) => isTeamVisible(teamId)),
  );
  const selectedPermissionIds = new Set(
    (employeeDetails.permissions ?? []).map((permission) =>
      Number(permission.role_id),
    ),
  );
  const savedPermissionIds = new Set(
    (employeeData?.permissions ?? []).map((permission) =>
      Number(permission.role_id),
    ),
  );

  const canChangeTeamMembership = Boolean(canEditTeam) && !isViewOnly;
  const teamOptions = [...teamsWithPermissions]
    .filter((team) => {
      const teamId = Number(team.id);
      if (!isTeamVisible(teamId)) return false;
      if (canChangeTeamMembership || canViewOtherTeams) return true;
      return selectedTeamIds.has(teamId);
    })
    .map((team) => ({
      id: String(team.id),
      value: String(team.name),
    }))
    .sort((left, right) => left.value.localeCompare(right.value));

  const permissionsByTeam = (() => {
    const grouped = new Map<number, { id: string; value: string }[]>();
    teamsWithPermissions.forEach((team) => {
      const teamId = Number(team.id);
      if (!teamId) return;
      const list = (team.permissions || [])
        .map((permission) => ({
          id: String(permission.role_id),
          value: String(permission.role),
        }))
        .filter((permission) => Number(permission.id) > 0)
        .sort((left, right) => left.value.localeCompare(right.value));
      grouped.set(teamId, list);
    });
    return grouped;
  })();

  const savedTeamIds = new Set(
    (employeeData?.teams ?? []).map((team) => Number(team.id)),
  );

  const visiblePermissions = (() => {
    const byRole = new Map<number, { id: string; value: string; teams: string[] }>();
    Array.from(selectedTeamIds).forEach((teamId) => {
      if (!isTeamVisible(teamId)) return;
      const teamName = String(
        teamsWithPermissions.find((team) => Number(team.id) === teamId)?.name || "",
      ).trim();
      (permissionsByTeam.get(teamId) ?? []).forEach((child) => {
        const roleId = Number(child.id);
        const existing = byRole.get(roleId);
        if (!existing) {
          byRole.set(roleId, {
            ...child,
            teams: teamName ? [teamName] : [],
          });
          return;
        }
        if (teamName && !existing.teams.includes(teamName)) {
          existing.teams.push(teamName);
        }
      });
    });
    return Array.from(byRole.values())
      .map((permission) => ({
        ...permission,
        teams: [...permission.teams].sort((left, right) =>
          left.localeCompare(right),
        ),
      }))
      .sort((left, right) => left.value.localeCompare(right.value));
  })();
  const permissionNeedle = permissionQuery.trim().toLowerCase();
  const filteredPermissions = permissionNeedle
    ? visiblePermissions.filter((permission) => {
        if (permission.value.toLowerCase().includes(permissionNeedle)) return true;
        return permission.teams.some((team) =>
          team.toLowerCase().includes(permissionNeedle),
        );
      })
    : visiblePermissions;
  const selectedVisiblePermissionCount = visiblePermissions.filter((permission) =>
    selectedPermissionIds.has(Number(permission.id)),
  ).length;
  const selectedPermissionsLoading = Array.from(selectedTeamIds).some((teamId) =>
    loadingPermissionTeamIds.includes(teamId),
  );

  const commitEmployee = (next: Employee) => {
    employeeDetailsRef.current = next;
    setEmployeeDetails(next);
  };

  /**
   * Role ids mapped to the given teams in the loaded catalog.
   * A role listed on more than one team is included once.
   */
  const roleIdsMappedToTeams = (teamIds: number[]) => {
    const roleIds = new Set<number>();
    teamIds.forEach((teamId) => {
      (permissionsByTeam.get(teamId) ?? []).forEach((child) => {
        roleIds.add(Number(child.id));
      });
    });
    return roleIds;
  };

  /**
   * Checking a team only adds membership so its permissions can be shown.
   * Unchecking removes that team and drops permissions that no remaining
   * team still maps. A permission shared with another checked team stays.
   */
  const toggleTeam = (teamId: string, teamName: string) => {
    const numericId = Number(teamId);
    if (
      permissionsLoading ||
      teamsLocked ||
      isViewOnly ||
      !canEditTeamPermissions(numericId)
    ) {
      return;
    }
    const teamAlreadySelected = (employeeDetailsRef.current.teams ?? []).some(
      (team) => Number(team.id) === numericId,
    );
    if (!teamAlreadySelected) setPermissionsOpen(true);
    setEmployeeDetails((prev) => {
      const currentPermissions = prev.permissions ?? [];
      const currentTeams = prev.teams ?? [];
      const teamSelected = currentTeams.some(
        (team) => Number(team.id) === numericId,
      );

      if (teamSelected) {
        const remainingTeamIds = currentTeams
          .map((team) => Number(team.id))
          .filter((id) => id !== numericId);
        const stillMapped = roleIdsMappedToTeams(remainingTeamIds);
        const removedTeamRoles = roleIdsMappedToTeams([numericId]);
        const next = {
          ...prev,
          teams: currentTeams.filter((team) => Number(team.id) !== numericId),
          permissions: currentPermissions.filter((permission) => {
            const roleId = Number(permission.role_id);
            if (!removedTeamRoles.has(roleId)) return true;
            return stillMapped.has(roleId);
          }),
        };
        employeeDetailsRef.current = next;
        return next;
      }

      const next = {
        ...prev,
        teams: [
          ...currentTeams,
          { id: numericId, name: teamName, is_manager: false },
        ],
      };
      employeeDetailsRef.current = next;
      if (onLoadTeamPermissions) {
        void onLoadTeamPermissions([numericId]).catch(() => undefined);
      }
      return next;
    });
  };

  const toggleTeamManager = (teamId: string, teamName: string) => {
    if (!canEditTeam || isViewOnly) return;
    const numericId = Number(teamId);
    setEmployeeDetails((prev) => {
      const currentTeams = prev.teams ?? [];
      const existing = currentTeams.find((team) => Number(team.id) === numericId);
      const next = !existing
        ? {
            ...prev,
            teams: [
              ...currentTeams,
              { id: numericId, name: teamName, is_manager: true },
            ],
          }
        : {
            ...prev,
            teams: currentTeams.map((team) =>
              Number(team.id) === numericId
                ? { ...team, is_manager: !isManagerFlag(team.is_manager) }
                : team,
            ),
          };
      employeeDetailsRef.current = next;
      return next;
    });
  };

  /**
   * A shared permission can be edited when this person can edit at least one
   * checked, visible team that maps it.
   */
  const canEditPermission = (roleId: number) => {
    if (permissionsLocked) return false;
    return Array.from(selectedTeamIds).some((teamId) => {
      if (!isTeamVisible(teamId) || !canEditTeamPermissions(teamId)) return false;
      return (permissionsByTeam.get(teamId) ?? []).some(
        (child) => Number(child.id) === roleId,
      );
    });
  };

  const editableFilteredPermissions = filteredPermissions.filter((permission) =>
    canEditPermission(Number(permission.id)),
  );
  const selectedEditableCount = editableFilteredPermissions.filter((permission) =>
    selectedPermissionIds.has(Number(permission.id)),
  ).length;
  const allEditableSelected =
    editableFilteredPermissions.length > 0 &&
    selectedEditableCount === editableFilteredPermissions.length;

  const setFilteredPermissions = (select: boolean) => {
    if (permissionsLocked || editableFilteredPermissions.length === 0) return;
    const targetIds = new Set(
      editableFilteredPermissions.map((permission) => Number(permission.id)),
    );
    setEmployeeDetails((prev) => {
      const kept = (prev.permissions ?? []).filter(
        (permission) => !targetIds.has(Number(permission.role_id)),
      );
      const permissions = select
        ? [
            ...kept,
            ...editableFilteredPermissions.map(
              (permission) =>
                ({
                  role_id: Number(permission.id),
                  role: permission.value,
                }) as EmployeePermisson,
            ),
          ]
        : kept;
      const next = { ...prev, permissions };
      employeeDetailsRef.current = next;
      return next;
    });
  };

  const togglePermission = (roleId: string, roleName: string) => {
    const numericRoleId = Number(roleId);
    if (permissionsLocked || !canEditPermission(numericRoleId)) return;
    setEmployeeDetails((prev) => {
      const currentPermissions = prev.permissions ?? [];
      const isSelected = currentPermissions.some(
        (permission) => Number(permission.role_id) === numericRoleId,
      );
      const permissions = isSelected
        ? currentPermissions.filter(
            (permission) => Number(permission.role_id) !== numericRoleId,
          )
        : [
            ...currentPermissions,
            { role_id: numericRoleId, role: roleName } as EmployeePermisson,
          ];
      const next = { ...prev, permissions };
      employeeDetailsRef.current = next;
      return next;
    });
  };

  const handleEdit = () => {
    if (isSaving || isViewOnly || !onSave) return;
    const teamCount = (employeeDetails.teams ?? []).length;
    const permissionCount = (employeeDetails.permissions ?? []).length;
    if (teamCount < 1 || permissionCount < 1) {
      if (teamCount < 1) setTeamsOpen(true);
      if (permissionCount < 1) setPermissionsOpen(true);
      setAssignmentError(
        teamCount < 1 && permissionCount < 1
          ? "Select at least one team and one permission."
          : teamCount < 1
            ? "Select at least one team."
            : "Select at least one permission.",
      );
      return;
    }
    setAssignmentError(null);
    onSave(employeeDetails);
  };

  const formBaseline = employeeData || blankEmployee();
  const hasAssignmentChanges = assignmentChanged(employeeDetails, formBaseline);

  const resetLocalChanges = () => {
    copyBaselineRef.current = {};
    commitEmployee({
      ...employeeDetails,
      permissions: (formBaseline.permissions ?? []).map((permission) => ({
        ...permission,
      })),
      teams: (formBaseline.teams ?? []).map((team) => ({ ...team })),
    });
  };

  const resetPassword = async () => {
    if (!resetPw) return;
    try {
      setResetPasswordMessage(null);
      await resetPw(employeeDetails.email);
      setResetPasswordMessage({
        type: "success",
        text: "Reset password link sent.",
      });
    } catch (error: any) {
      setResetPasswordMessage({
        type: "error",
        text:
          error?.message || "Couldn't send the reset password link.",
      });
    }
  };

  const title = isAddMode
    ? "Add Employee"
    : isViewOnly
      ? "View Employee"
      : "Edit Employee";

  const form = (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "4px",
            background: `linear-gradient(90deg, ${theme.palette.customColors.blueGradientStart} 0%, ${theme.palette.customColors.blueGradientEnd} 50%, ${theme.palette.customColors.blueGradientStart} 100%)`,
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: { xs: "12px 16px 12px", sm: "16px 20px 16px" },
            borderBottom: `1px solid ${theme.palette.customColors.greyBorder}`,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {layout === "page" && (
              <IconButton
                aria-label="Back to employees"
                size="small"
                onClick={onClose ?? (() => {})}
                sx={{
                  border: `1px solid ${theme.palette.customColors.greyBorder}`,
                  borderRadius: 2,
                  color: theme.palette.customColors.greyTextColor,
                  "&:hover": {
                    backgroundColor: theme.palette.customColors.lightGreyColor,
                    color: theme.palette.customColors.blackBorder,
                  },
                }}
              >
                <ArrowBackIosNewIcon sx={{ fontSize: 16 }} />
              </IconButton>
            )}
            <CustomTypography variant="heading3">{title}</CustomTypography>
          </Box>
          {layout === "dialog" && (
            <IconButton
              aria-label="close"
              onClick={onClose ?? (() => {})}
              sx={{
                color: theme.palette.customColors.greyTextColor,
                "&:hover": {
                  backgroundColor: theme.palette.customColors.lightGreyColor,
                  color: theme.palette.customColors.blackBorder,
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          )}
        </Box>

        <Box
          sx={{
            padding: { xs: "20px", sm: "24px" },
            flex: 1,
            overflow: "auto",
            maxHeight: layout === "page" ? "none" : "60vh",
            "&::-webkit-scrollbar": { width: "8px" },
            "&::-webkit-scrollbar-track": {
              backgroundColor: theme.palette.customColors.greyBackground,
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: theme.palette.customColors.greyDarkborder,
              borderRadius: "4px",
              "&:hover": {
                backgroundColor: theme.palette.customColors.greyTextColor,
              },
            },
          }}
        >
          <CustomTypography
            variant="heading6"
            style={{ fontWeight: 600, marginBottom: 16 }}
          >
            Basic Info
          </CustomTypography>
          <Grid2 container spacing={2} sx={{ mb: 3 }}>
            <Grid2 size={{ xs: 12, md: 4 }}>
              <FormSmallTextField
                sx={{ mb: 0 }}
                id="name"
                label="Name"
                fontSize="0.9rem"
                value={employeeDetails.name ?? ""}
                onChange={(_, value) => handleFieldChange("name", value ?? "")}
                disabled={profileLocked}
              />
            </Grid2>
            <Grid2 size={{ xs: 12, md: 4 }}>
              <FormSmallTextField
                sx={{ mb: 0 }}
                id="email"
                label="Email"
                type="email"
                fontSize="0.9rem"
                value={employeeDetails.email ?? ""}
                onChange={(_, value) => handleFieldChange("email", value ?? "")}
                disabled={isViewOnly || !isAddMode}
              />
            </Grid2>
            <Grid2 size={{ xs: 12, md: 4 }}>
              <FormSmallTextField
                sx={{ mb: 0 }}
                id="discount_percentage"
                label="Discount"
                fontSize="0.9rem"
                value={
                  employeeDetails.discount_percentage === undefined ||
                  employeeDetails.discount_percentage === null
                    ? ""
                    : String(employeeDetails.discount_percentage)
                }
                onChange={(_, value) =>
                  handleFieldChange(
                    "discount_percentage",
                    (value ?? "").trim() === "" ? "" : value ?? "",
                  )
                }
                disabled={Boolean(isViewOnly) || Boolean(discountPermission)}
              />
            </Grid2>
          </Grid2>

          <Divider sx={{ my: 3 }} />
          <CustomTypography
            variant="heading6"
            style={{ fontWeight: 600, marginBottom: 16 }}
          >
            Contact
          </CustomTypography>
          <Box display="flex" flexWrap="wrap" gap={2} sx={{ mb: 3 }}>
            <Box sx={{ flex: "0 1 120px" }}>
              <FormSmallTextField
                sx={{ mb: 0 }}
                id="calling_code"
                label="Calling Code"
                fontSize="0.9rem"
                value={employeeDetails.calling_code ?? ""}
                onChange={(_, value) =>
                  handleFieldChange("calling_code", value ?? "")
                }
                disabled={profileLocked}
              />
            </Box>
            <Box sx={{ flex: "1 1 200px" }}>
              <FormSmallTextField
                sx={{ mb: 0 }}
                id="phone_number"
                label="Phone Number"
                fontSize="0.9rem"
                value={employeeDetails.phone_number ?? ""}
                onChange={(_, value) =>
                  handleFieldChange("phone_number", value ?? "")
                }
                disabled={profileLocked}
              />
            </Box>
            {!isAddMode && resetPw && (
              <Box sx={{ alignSelf: "flex-end" }}>
                <AppButton
                  variant="secondary"
                  onClick={resetPassword}
                  label="Reset Password"
                />
              </Box>
            )}
          </Box>
          {resetPasswordMessage ? (
            <CustomTypography
              variant="body3"
              color={
                resetPasswordMessage.type === "success"
                  ? theme.palette.customColors.successGreen
                  : theme.palette.customColors.errorRed
              }
              style={{ display: "block", marginBottom: 8 }}
            >
              {resetPasswordMessage.text}
            </CustomTypography>
          ) : null}

          <Divider sx={{ my: 3 }} />
          <CustomTypography
            variant="heading6"
            style={{ fontWeight: 600, marginBottom: 16 }}
          >
            Project
          </CustomTypography>
          <Box sx={{ mb: 3 }}>
            <AppSearchDropdownMulti
              borderColor={theme.palette.customColors.blackBorder}
              isMultiSelect={false}
              title="Select Project"
              fontSize="0.8rem"
              value={PERMISSION_PROJECT_OPTIONS.filter(
                (item) => item.id === permissionProject,
              )}
              list={PERMISSION_PROJECT_OPTIONS}
              local={true}
              handleSelect={(selected) => {
                const next = selected[0]?.id as PermissionProject | undefined;
                if (
                  next &&
                  (next === "swageazy" || next === "swayy") &&
                  onPermissionProjectChange
                ) {
                  onPermissionProjectChange(next);
                }
              }}
              disabled={
                permissionsLoading || isSaving || !onPermissionProjectChange
              }
            />
            {permissionsError ? (
              <Box sx={{ mt: 1 }}>
                <InlineFormError message={permissionsError} />
              </Box>
            ) : null}
          </Box>

          {canEditTeam && !isViewOnly && onSearchEmployees && onLoadCopySource ? (
            <>
              <Divider sx={{ my: 3 }} />
              <CopyEmployeePermissions
                permissionProject={permissionProject}
                resetKey={copyResetKey}
                disabled={permissionsLoading}
                teamsWithPermissions={teamsWithPermissions}
                onEnsureTeamPermissions={onLoadTeamPermissions}
                onSearchEmployees={onSearchEmployees}
                onLoadEmployee={onLoadCopySource}
                onCopyTeamsChange={(change) => {
                  const prev = employeeDetailsRef.current;
                  const currentTeams = prev.teams ?? [];
                  const currentPermissions = prev.permissions ?? [];
                  const currentRoleIds = new Set(
                    currentPermissions.map((permission) =>
                      Number(permission.role_id),
                    ),
                  );
                  const teamWasSelected = (teamId: number) =>
                    currentTeams.some((team) => Number(team.id) === teamId);

                  change.addTeams.forEach((team) => {
                    const copiedTeamId = Number(team.id);
                    if (!copyBaselineRef.current[copiedTeamId]) {
                      const alreadyShown = (
                        permissionsByTeam.get(copiedTeamId) ?? []
                      )
                        .map((child) => Number(child.id))
                        .filter((roleId) => currentRoleIds.has(roleId));
                      copyBaselineRef.current[copiedTeamId] = {
                        teamSelected: teamWasSelected(copiedTeamId),
                        roleIds: alreadyShown,
                      };
                    }
                  });

                  let nextTeams = currentTeams.filter((team) => {
                    const teamId = Number(team.id);
                    if (!change.removeTeamIds.includes(teamId)) return true;
                    return Boolean(
                      copyBaselineRef.current[teamId]?.teamSelected,
                    );
                  });
                  const keepRoleIds = new Set<number>();
                  Object.values(copyBaselineRef.current).forEach((baseline) => {
                    baseline.roleIds.forEach((roleId) => keepRoleIds.add(roleId));
                  });
                  change.removeTeamIds.forEach((removedTeamId) => {
                    const baseline = copyBaselineRef.current[removedTeamId];
                    baseline?.roleIds.forEach((roleId) => keepRoleIds.add(roleId));
                    delete copyBaselineRef.current[removedTeamId];
                  });
                  const removeRoleIds = new Set(
                    change.removeRoleIds.filter(
                      (roleId) => !keepRoleIds.has(Number(roleId)),
                    ),
                  );
                  let nextPermissions = currentPermissions.filter(
                    (permission) =>
                      !removeRoleIds.has(Number(permission.role_id)),
                  );
                  const roleIds = new Set(
                    nextPermissions.map((permission) =>
                      Number(permission.role_id),
                    ),
                  );
                  const teamIds = new Set(
                    nextTeams.map((team) => Number(team.id)),
                  );
                  change.addTeams.forEach((team) => {
                    const copiedTeamId = Number(team.id);
                    if (!teamIds.has(copiedTeamId)) {
                      nextTeams = [
                        ...nextTeams,
                        {
                          id: copiedTeamId,
                          name: team.name,
                          is_manager: Boolean(team.is_manager),
                        },
                      ];
                      teamIds.add(copiedTeamId);
                    }
                  });
                  change.addPermissions.forEach((permission) => {
                    const copiedRoleId = Number(permission.role_id);
                    if (roleIds.has(copiedRoleId)) return;
                    nextPermissions = [
                      ...nextPermissions,
                      {
                        role_id: copiedRoleId,
                        role: permission.role,
                      } as EmployeePermisson,
                    ];
                    roleIds.add(copiedRoleId);
                  });
                  commitEmployee({
                    ...prev,
                    teams: nextTeams,
                    permissions: nextPermissions,
                  });
                }}
              />
            </>
          ) : null}

          <Box
            sx={{
              mt: 3,
              border: `1px solid ${theme.palette.customColors.greyBorder}`,
              borderRadius: 2,
              overflow: "hidden",
              backgroundColor: theme.palette.customColors.white,
            }}
          >
          <Box
            onClick={() => setTeamsOpen((open) => !open)}
            sx={{
              display: "flex",
              alignItems: { xs: "flex-start", sm: "center" },
              justifyContent: "space-between",
              gap: 1,
              flexWrap: "wrap",
              px: { xs: 1.25, sm: 2 },
              py: 1.25,
              cursor: "pointer",
              backgroundColor: theme.palette.customColors.lightGreyColor,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.25, minWidth: 0, flex: "1 1 220px" }}>
              <IconButton
                size="small"
                aria-label={teamsOpen ? "Collapse teams" : "Expand teams"}
                sx={{ color: theme.palette.customColors.greyTextColor, mt: 0.15 }}
              >
                {teamsOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
              <Box sx={{ minWidth: 0 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                  <CustomTypography variant="heading6" style={{ fontWeight: 600 }}>
                    Teams ({selectedTeamIds.size}/{teamOptions.length})
                  </CustomTypography>
                  {permissionsLoading ? <CircularProgress size={16} /> : null}
                </Box>
                <CustomTypography
                  variant="body3"
                  color={theme.palette.customColors.greyTextColor}
                  style={{ display: "block", marginTop: 2 }}
                >
                  {isViewOnly
                    ? "These are the teams you can see for this person."
                    : teamsLocked
                      ? "Team membership is view only."
                      : "Check a team to show its permissions."}
                </CustomTypography>
              </Box>
            </Box>
            {!isViewOnly && (
              <Box onClick={(event) => event.stopPropagation()} sx={{ flexShrink: 0 }}>
                <AppButton
                  variant="transparentPrimary"
                  label="Reset"
                  disabled={!hasAssignmentChanges}
                  onClick={resetLocalChanges}
                  customStyles={{ flexShrink: 0 }}
                />
              </Box>
            )}
          </Box>
          <Collapse in={teamsOpen} timeout="auto">
          <Box sx={{ px: { xs: 1.25, sm: 2 }, py: { xs: 1.5, sm: 2 } }}>
          {teamOptions.length === 0 ? (
            <CustomTypography
              variant="body3"
              color={theme.palette.customColors.greyTextColor}
            >
              No teams are available.
            </CustomTypography>
          ) : (
            <Grid2 container spacing={1.5} sx={{ mb: 1 }}>
              {teamOptions.map((team) => {
                const teamId = Number(team.id);
                const teamSelected = selectedTeamIds.has(teamId);
                const membership = (employeeDetails.teams ?? []).find(
                  (item) => Number(item.id) === teamId,
                );
                const managerOn = isManagerFlag(membership?.is_manager);
                const canEditThisTeam = canEditTeamPermissions(teamId);
                const canToggleTeam =
                  !permissionsLoading &&
                  !isViewOnly &&
                  !teamsLocked &&
                  canEditThisTeam;
                const teamState: PermissionEditState = teamSelected
                  ? savedTeamIds.has(teamId)
                    ? "saved"
                    : "new"
                  : savedTeamIds.has(teamId)
                    ? "removed"
                    : "idle";
                return (
                  <Grid2 key={team.id} size={{ xs: 12, sm: 6, lg: 4 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 0.5,
                        px: 1,
                        py: 0.75,
                        minHeight: 44,
                        borderRadius: 1.5,
                        border: `1px solid ${
                          teamSelected
                            ? theme.palette.primary.main
                            : permissionBorderColor(theme, teamState)
                        }`,
                        backgroundColor: teamSelected
                          ? theme.palette.customColors.lightGreyColor
                          : theme.palette.customColors.white,
                        opacity: canToggleTeam || canEditTeam ? 1 : 0.7,
                      }}
                    >
                      <Checkbox
                        size="small"
                        checked={teamSelected}
                        disabled={!canToggleTeam}
                        onClick={() => toggleTeam(team.id, team.value)}
                        inputProps={{ "aria-label": team.value }}
                        sx={permissionCheckboxSx(theme, teamState)}
                      />
                      <CustomTypography
                        variant="body2"
                        style={{ fontWeight: 600, minWidth: 0, flex: "1 1 120px" }}
                      >
                        {team.value}
                      </CustomTypography>
                      <Box
                        sx={{
                          marginLeft: "auto",
                          display: "flex",
                          alignItems: "center",
                          gap: 0.25,
                          flexShrink: 0,
                        }}
                      >
                        <Checkbox
                          size="small"
                          checked={managerOn}
                          disabled={!canEditTeam || isViewOnly || !teamSelected}
                          onClick={() => toggleTeamManager(team.id, team.value)}
                          inputProps={{
                            "aria-label": `Manager of ${team.value}`,
                          }}
                          sx={{ p: 0.5 }}
                        />
                        <CustomTypography
                          variant="body3"
                          color={theme.palette.customColors.greyTextColor}
                        >
                          Manager
                        </CustomTypography>
                      </Box>
                    </Box>
                  </Grid2>
                );
              })}
            </Grid2>
          )}
          </Box>
          </Collapse>
          </Box>

          <Box
            sx={{
              mt: 2,
              border: `1px solid ${theme.palette.customColors.greyBorder}`,
              borderRadius: 2,
              overflow: "hidden",
              backgroundColor: theme.palette.customColors.white,
            }}
          >
          <Box
            onClick={() => setPermissionsOpen((open) => !open)}
            sx={{
              display: "flex",
              alignItems: { xs: "flex-start", sm: "center" },
              justifyContent: "space-between",
              gap: 1,
              flexWrap: "wrap",
              px: { xs: 1.25, sm: 2 },
              py: 1.25,
              cursor: "pointer",
              backgroundColor: theme.palette.customColors.lightGreyColor,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.25, minWidth: 0, flex: "1 1 220px" }}>
              <IconButton
                size="small"
                aria-label={permissionsOpen ? "Collapse permissions" : "Expand permissions"}
                sx={{ color: theme.palette.customColors.greyTextColor, mt: 0.15 }}
              >
                {permissionsOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
              <Box sx={{ minWidth: 0 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                  <CustomTypography variant="heading6" style={{ fontWeight: 600 }}>
                    Permissions ({selectedVisiblePermissionCount}/{visiblePermissions.length})
                  </CustomTypography>
                  {selectedPermissionsLoading ? <CircularProgress size={16} /> : null}
                </Box>
                <CustomTypography
                  variant="body3"
                  color={theme.palette.customColors.greyTextColor}
                  style={{ display: "block", marginTop: 2 }}
                >
                  {isViewOnly
                    ? "These permissions belong to the teams you can see."
                    : "Each permission is listed once."}
                </CustomTypography>
              </Box>
            </Box>
            <Box
              onClick={(event) => event.stopPropagation()}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: { xs: "flex-start", sm: "flex-end" },
                gap: 1,
                flexWrap: "wrap",
                width: { xs: "100%", sm: "auto" },
                pl: { xs: 4.5, sm: 0 },
              }}
            >
              {editableFilteredPermissions.length > 0 ? (
                <Box
                  onClick={() => setFilteredPermissions(!allEditableSelected)}
                  sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}
                >
                  <Checkbox
                    size="small"
                    checked={allEditableSelected}
                    indeterminate={selectedEditableCount > 0 && !allEditableSelected}
                    inputProps={{ "aria-label": "Select all permissions" }}
                    sx={{ p: 0.5, pointerEvents: "none" }}
                  />
                  <CustomTypography
                    variant="body3"
                    color={theme.palette.customColors.greyTextColor}
                  >
                    {permissionNeedle ? "Select shown" : "Select all"}
                  </CustomTypography>
                </Box>
              ) : null}
              {visiblePermissions.length > 0 ? (
                <PermissionCatalogSearch onDebouncedChange={setDebouncedPermissionQuery} />
              ) : null}
            </Box>
          </Box>
          <Collapse in={permissionsOpen} timeout="auto">
          <Box sx={{ px: { xs: 1.25, sm: 2 }, py: { xs: 1.5, sm: 2 } }}>
          {selectedTeamIds.size === 0 ? (
            <CustomTypography
              variant="body3"
              color={theme.palette.customColors.greyTextColor}
            >
              Check a team to see its permissions.
            </CustomTypography>
          ) : (permissionsLoading || selectedPermissionsLoading) &&
            visiblePermissions.length === 0 ? (
            <CustomTypography
              variant="body3"
              color={theme.palette.customColors.greyTextColor}
            >
              Loading permissions...
            </CustomTypography>
          ) : visiblePermissions.length === 0 ? (
            <CustomTypography
              variant="body3"
              color={theme.palette.customColors.greyTextColor}
            >
              No permissions are mapped to the checked teams.
            </CustomTypography>
          ) : filteredPermissions.length === 0 ? (
            <CustomTypography
              variant="body3"
              color={theme.palette.customColors.greyTextColor}
            >
              No permissions match this search.
            </CustomTypography>
          ) : (
            <Grid2 container spacing={1} sx={{ mb: 1 }}>
              {filteredPermissions.map((permission) => {
                const roleId = Number(permission.id);
                const checked = selectedPermissionIds.has(roleId);
                const canPickPermission = canEditPermission(roleId);
                const editState = permissionEditState(
                  checked,
                  savedPermissionIds.has(roleId),
                );
                return (
                  <Grid2
                    key={permission.id}
                    size={{ xs: 12, sm: 6, md: 4, lg: 3 }}
                  >
                    <Box
                      onClick={() => {
                        if (!canPickPermission) return;
                        togglePermission(permission.id, permission.value);
                      }}
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 0.5,
                        px: 1,
                        py: 0.75,
                        height: "100%",
                        borderRadius: 1.5,
                        border: `1px solid ${permissionBorderColor(theme, editState)}`,
                        backgroundColor:
                          editState === "saved"
                            ? theme.palette.customColors.successGreenLightBackground
                            : checked
                              ? theme.palette.customColors.lightGreyColor
                              : theme.palette.customColors.white,
                        cursor: canPickPermission ? "pointer" : "default",
                        opacity: canPickPermission ? 1 : 0.7,
                      }}
                    >
                      <Checkbox
                        size="small"
                        checked={checked}
                        disabled={!canPickPermission}
                        sx={{
                          ...permissionCheckboxSx(theme, editState),
                          pointerEvents: "none",
                          mt: 0.15,
                        }}
                      />
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <CustomTypography variant="body2">
                          {permission.value}
                        </CustomTypography>
                        {permission.teams.length > 0 ? (
                          <CustomTypography
                            variant="body3"
                            color={theme.palette.customColors.greyTextColor}
                            style={{ display: "block", marginTop: 2 }}
                          >
                            {permissionTeamLabel(permission.teams)}
                          </CustomTypography>
                        ) : null}
                      </Box>
                    </Box>
                  </Grid2>
                );
              })}
            </Grid2>
          )}
          </Box>
          </Collapse>
          </Box>
        </Box>

        {(!isViewOnly || layout === "dialog") && (
        <Box
          sx={{
            display: "flex",
            padding: { xs: "16px 20px 20px", sm: "20px 24px 24px" },
            borderTop: `1px solid ${theme.palette.customColors.greyBorder}`,
            backgroundColor: theme.palette.customColors.lightGreyColor,
            gap: 2,
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>
            {assignmentError || saveError ? (
              <InlineFormError message={assignmentError || saveError || ""} />
            ) : null}
          </Box>
          <Box sx={{ display: "flex", gap: 2 }}>
            {layout === "dialog" && (
              <AppButton
                variant="grayscale"
                onClick={onClose ?? (() => {})}
                label="Cancel"
                disabled={isSaving}
              />
            )}
            {!isViewOnly && (
              <AppButton
                variant="primary"
                onClick={handleEdit}
                label={isSaving ? "Saving…" : "Save"}
                disabled={isSaving}
              />
            )}
          </Box>
        </Box>
        )}
      </Box>
    </>
  );

  if (layout === "page") {
    return (
      <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12 }} sx={{ px: 2 }}>
        <Paper
          sx={{
            mt: 3,
            borderRadius: 2,
            overflow: "hidden",
            boxShadow:
              "rgba(0, 0, 0, 0.15) 0px 4px 24px, rgba(0, 0, 0, 0.05) 0px 8px 32px",
          }}
        >
          {form}
        </Paper>
      </Grid2>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason !== "backdropClick" && onClose) onClose();
      }}
      fullWidth
      maxWidth="sm"
      slotProps={{
        paper: {
          sx: {
            borderRadius: "16px",
            boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
            overflow: "hidden",
            maxHeight: "90vh",
            margin: { xs: "16px", sm: "24px" },
            background: `linear-gradient(135deg, ${theme.palette.customColors.white} 0%, ${theme.palette.customColors.lightGreyColor} 100%)`,
          },
        },
      }}
      sx={{ zIndex: 2000 }}
    >
      {form}
    </Dialog>
  );
};

export default EditEmployeePopup;
